import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createProject, updateProject, getStates, uploadSiteImage, getCouncilsForState, getSiteImageUrl } from '../../services/api';
import NSWEnquiryModal from '../NSWEnquiryModal';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Step1ProjectDetails = ({ project, onComplete }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    plan_type: 'operational_wmp',
    development_type: '',
    project_name: '',
    project_description: '',
    site_address: '',
    state_code: '',
    council_name: '',
    client_name: '',
    collections_per_week: 1,
    collection_type: 'council',
    // Mixed-Use specific fields
    commercial_collections_per_week: 1,
    commercial_collection_type: 'private',
    bin_storage_type: 'shared'
  });
  const [states, setStates] = useState([]);
  const [councils, setCouncils] = useState([]);
  const [councilsLoading, setCouncilsLoading] = useState(false);
  const [siteImage, setSiteImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [showNSWModal, setShowNSWModal] = useState(false);
  const [pricing, setPricing] = useState(null);

  // Check if Mixed-Use development type
  const isMixedUse = formData.development_type === 'Mixed use';

  // Plan type options
  const planTypes = [
    { value: 'operational_wmp', label: 'Operational WMP (OpWMP)', description: 'For ongoing waste management operations' },
    { value: 'demolition_construction_wmp', label: 'Demolition & Construction WMP (D&CWMP)', description: 'For demolition and construction phases' },
    { value: 'construction_management_plan', label: 'Construction Management Plan (CMP)', description: 'Construction site management - no demolition' },
    { value: 'bundled_wmp', label: 'Bundled WMP Pack (OpWMP + D&CWMP)', description: 'Complete package - save when you need both plans' },
  ];

  // Load pricing on mount
  useEffect(() => {
    const loadPricing = async () => {
      try {
        const response = await axios.get(`${API}/api/pricing`);
        setPricing(response.data);
      } catch (err) {
        console.error('Failed to load pricing:', err);
      }
    };
    loadPricing();
  }, []);

  // Calculate current price
  const getCurrentPrice = () => {
    if (!pricing || !formData.development_type) return null;
    
    let planKey = 'opwmp';
    if (formData.plan_type === 'demolition_construction_wmp') planKey = 'dcwmp';
    if (formData.plan_type === 'bundled_wmp') planKey = 'bundled';
    if (formData.plan_type === 'construction_management_plan') planKey = 'cmp';
    
    return pricing[planKey]?.[formData.development_type];
  };

  useEffect(() => {
    loadStates();
    if (project) {
      setFormData({
        plan_type: project.plan_type || 'operational_wmp',
        development_type: project.development_type || '',
        project_name: project.project_name || '',
        project_description: project.project_description || '',
        site_address: project.site_address || '',
        state_code: project.state_code || '',
        council_name: project.council_name || '',
        client_name: project.client_name || '',
        collections_per_week: project.collections_per_week || 1,
        collection_type: project.collection_type || 'council',
        // Mixed-Use specific fields
        commercial_collections_per_week: project.commercial_collections_per_week || 1,
        commercial_collection_type: project.commercial_collection_type || 'private',
        bin_storage_type: project.bin_storage_type || 'shared'
      });
      // Load councils if state is already selected
      if (project.state_code) {
        loadCouncils(project.state_code);
      }
      // Load existing site image if available
      if (project.site_location_image || project.site_location_image_path) {
        const token = localStorage.getItem('opwmp_token');
        const imageUrl = getSiteImageUrl(project.id);
        // Fetch the image with auth header and create object URL for preview
        fetch(imageUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
          .then(response => {
            if (response.ok) return response.blob();
            throw new Error('Image not found');
          })
          .then(blob => {
            const objectUrl = URL.createObjectURL(blob);
            setImagePreview(objectUrl);
          })
          .catch(err => {
            console.log('No existing site image or failed to load:', err);
          });
      }
    }
  }, [project]);

  const loadStates = async () => {
    try {
      const data = await getStates();
      setStates(data);
    } catch (error) {
      console.error('Failed to load states:', error);
    }
  };

  const loadCouncils = async (stateCode) => {
    if (!stateCode) {
      setCouncils([]);
      return;
    }
    setCouncilsLoading(true);
    try {
      const data = await getCouncilsForState(stateCode);
      if (data.supported && data.councils.length > 0) {
        setCouncils(data.councils);
      } else {
        setCouncils([]);
      }
    } catch (error) {
      console.error('Failed to load councils:', error);
      setCouncils([]);
    } finally {
      setCouncilsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Convert numeric fields to integers
    const numericFields = ['collections_per_week', 'commercial_collections_per_week'];
    const processedValue = numericFields.includes(name) ? parseInt(value, 10) : value;
    
    setFormData({
      ...formData,
      [name]: processedValue
    });
    
    // If state changed, load councils for that state
    if (name === 'state_code') {
      // Check if NSW is selected - show modal instead
      if (value === 'NSW') {
        setShowNSWModal(true);
        // Reset the state selection since we're showing the modal
        setFormData(prev => ({ ...prev, state_code: '', council_name: '' }));
        return;
      }
      setFormData(prev => ({ ...prev, council_name: '' })); // Reset council
      loadCouncils(value);
    }
  };

  // Handle file validation and processing
  const processFile = useCallback((file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/tiff', 'image/tif', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a JPEG, TIFF, or PNG image');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be smaller than 10MB');
      return;
    }

    setSiteImage(file);
    setError('');
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    processFile(file);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let projectData;
      
      // Ensure numeric fields are properly converted to integers
      const sanitizedFormData = {
        ...formData,
        collections_per_week: parseInt(formData.collections_per_week, 10) || 1,
        commercial_collections_per_week: parseInt(formData.commercial_collections_per_week, 10) || 1
      };
      
      if (project) {
        // Editing existing project - update all Step 1 data
        const updatedProject = await updateProject(project.id, sanitizedFormData);
        
        // Upload site image if provided
        if (siteImage) {
          await uploadSiteImage(project.id, siteImage);
        }
        
        // Use the returned updated project data
        projectData = updatedProject;
        
        // Call onComplete to advance to next step
        onComplete(projectData);
      } else {
        // Creating new project
        projectData = await createProject(sanitizedFormData);
        
        // Upload site image if provided
        if (siteImage) {
          await uploadSiteImage(projectData.id, siteImage);
        }
        
        // Navigate to the project wizard with the new project ID
        // Don't call onComplete here - the navigation will reload and set the step
        navigate(`/project/${projectData.id}`);
      }
    } catch (error) {
      console.error('Error saving project:', error);
      setError(error.response?.data?.detail || 'Failed to save project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-8 py-6 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900">Step 1: Project Details</h2>
        <p className="mt-1 text-sm text-gray-600">
          Provide basic information about your waste management project
        </p>
      </div>

      <form onSubmit={handleSubmit} className="px-8 py-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Plan Type Selection */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <label className="block text-sm font-medium text-blue-800 mb-3">
            Plan Package <span className="text-red-500">*</span>
          </label>
          
          {/* Free Plan Notice */}
          <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-md">
            <p className="text-xs text-green-700 flex items-center">
              <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
              <span><strong>New customers:</strong> Your first single plan (OpWMP, D&CWMP, or CMP) is FREE! Bundled packs are not included in this offer.</span>
            </p>
          </div>
          
          <div className="space-y-3">
            {planTypes.map((type) => (
              <label
                key={type.value}
                className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${
                  formData.plan_type === type.value
                    ? 'border-blue-500 bg-blue-100 ring-2 ring-blue-500'
                    : 'border-gray-200 bg-white hover:border-blue-300'
                }`}
              >
                <input
                  type="radio"
                  name="plan_type"
                  value={type.value}
                  checked={formData.plan_type === type.value}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <div className="ml-3 flex-1">
                  <div className="flex justify-between items-start">
                    <span className="text-sm font-medium text-gray-900">{type.label}</span>
                    {pricing && formData.development_type && (
                      <span className="text-sm font-bold text-blue-600">
                        ${pricing[type.value === 'operational_wmp' ? 'opwmp' : type.value === 'demolition_construction_wmp' ? 'dcwmp' : type.value === 'construction_management_plan' ? 'cmp' : 'bundled']?.[formData.development_type] || '—'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{type.description}</p>
                  {type.value === 'bundled_wmp' && pricing && formData.development_type && (
                    <p className="text-xs text-green-600 font-medium mt-1">
                      Save ${(pricing.opwmp?.[formData.development_type] || 0) + (pricing.dcwmp?.[formData.development_type] || 0) - (pricing.bundled?.[formData.development_type] || 0)} vs buying separately
                    </p>
                  )}
                  {type.value !== 'bundled_wmp' && (
                    <p className="text-xs text-green-600 font-medium mt-1">
                      ✓ Eligible for free first plan
                    </p>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Development Type - Moved here, right after Plan Type */}
        <div>
          <label htmlFor="development_type" className="block text-sm font-medium text-gray-700">
            Development Type <span className="text-red-500">*</span>
          </label>
          <select
            id="development_type"
            name="development_type"
            required
            value={formData.development_type}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select type...</option>
            <option value="Residential">Residential</option>
            <option value="Commercial">Commercial</option>
            <option value="Mixed use">Mixed Use</option>
          </select>
          <p className="mt-1 text-xs text-gray-500">This determines how waste generation is calculated and affects pricing</p>
        </div>

        {/* Price Display */}
        {getCurrentPrice() && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">Selected Plan Price</p>
                <p className="text-xs text-green-600">
                  {planTypes.find(p => p.value === formData.plan_type)?.label} • {formData.development_type}
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-green-700">${getCurrentPrice()}</p>
                <p className="text-xs text-green-600">AUD (inc. GST)</p>
              </div>
            </div>
          </div>
        )}

        <div>
          <label htmlFor="project_name" className="block text-sm font-medium text-gray-700">
            Project Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="project_name"
            name="project_name"
            required
            value={formData.project_name}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g., 22 Langdon Street Development"
          />
        </div>

        <div>
          <label htmlFor="client_name" className="block text-sm font-medium text-gray-700">
            Client/Developer Name
          </label>
          <input
            type="text"
            id="client_name"
            name="client_name"
            value={formData.client_name}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g., Bob Jones"
          />
        </div>

        <div>
          <label htmlFor="project_description" className="block text-sm font-medium text-gray-700">
            Project Description <span className="text-red-500">*</span>
          </label>
          <textarea
            id="project_description"
            name="project_description"
            required
            rows={4}
            value={formData.project_description}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Provide a detailed description of the development..."
          />
          <p className="mt-1 text-sm text-gray-500">This will appear in Section 2 of your WMP</p>
        </div>

        <div>
          <label htmlFor="site_address" className="block text-sm font-medium text-gray-700">
            Site Address <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="site_address"
            name="site_address"
            required
            value={formData.site_address}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g., 22 Langdon Street, Perth, WA 6000"
          />
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label htmlFor="state_code" className="block text-sm font-medium text-gray-700">
              State/Territory <span className="text-red-500">*</span>
            </label>
            <select
              id="state_code"
              name="state_code"
              required
              value={formData.state_code}
              onChange={handleChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a state...</option>
              {states.map((state) => (
                <option key={state.code} value={state.code}>
                  {state.name} ({state.code})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="council_name" className="block text-sm font-medium text-gray-700">
              Local Council/LGA <span className="text-red-500">*</span>
            </label>
            {councilsLoading ? (
              <div className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
                Loading councils...
              </div>
            ) : councils.length > 0 ? (
              <>
                <select
                  id="council_name"
                  name="council_name"
                  required
                  value={formData.council_name}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a council...</option>
                  {councils.map((council) => (
                    <option key={council} value={council}>
                      {council}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">Select your local council from the list</p>
              </>
            ) : (
              <>
                <input
                  type="text"
                  id="council_name"
                  name="council_name"
                  required
                  value={formData.council_name}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Blacktown City Council"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.state_code ? 'Enter your local council name' : 'Select a state first to see available councils'}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Collection Settings */}
        <div className="border-t border-gray-200 pt-6 mt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {isMixedUse ? 'Residential Collection Settings' : 'Collection Settings'}
          </h3>
          {isMixedUse && (
            <p className="text-sm text-gray-600 mb-4">
              For Mixed-Use developments, residential and commercial waste are collected separately.
            </p>
          )}
          
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label htmlFor="collections_per_week" className="block text-sm font-medium text-gray-700">
                {isMixedUse ? 'Residential Collections Per Week' : 'Collections Per Week'} <span className="text-red-500">*</span>
              </label>
              <select
                id="collections_per_week"
                name="collections_per_week"
                required
                value={formData.collections_per_week}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={1}>1 collection per week</option>
                <option value={2}>2 collections per week</option>
                <option value={3}>3 collections per week</option>
                <option value={4}>4 collections per week</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {isMixedUse ? 'How often will residential bins be collected?' : 'How often will bins be collected?'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isMixedUse ? 'Residential Collection Type' : 'Collection Type'} <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="collection_type"
                    value="council"
                    checked={formData.collection_type === 'council'}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Council Collection</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="collection_type"
                    value="private"
                    checked={formData.collection_type === 'private'}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Private Collection</span>
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {isMixedUse ? 'Who will collect residential waste?' : 'Who will collect the waste?'}
              </p>
            </div>
          </div>
        </div>

        {/* Commercial Collection Settings - Only for Mixed-Use */}
        {isMixedUse && (
          <div className="border-t border-gray-200 pt-6 mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Commercial Collection Settings</h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label htmlFor="commercial_collections_per_week" className="block text-sm font-medium text-gray-700">
                  Commercial Collections Per Week <span className="text-red-500">*</span>
                </label>
                <select
                  id="commercial_collections_per_week"
                  name="commercial_collections_per_week"
                  required
                  value={formData.commercial_collections_per_week}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={1}>1 collection per week</option>
                  <option value={2}>2 collections per week</option>
                  <option value={3}>3 collections per week</option>
                  <option value={4}>4 collections per week</option>
                  <option value={5}>5 collections per week</option>
                  <option value={6}>6 collections per week</option>
                  <option value={7}>7 collections per week</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">How often will commercial bins be collected?</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commercial Collection Type <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="commercial_collection_type"
                      value="council"
                      checked={formData.commercial_collection_type === 'council'}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Council Collection</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="commercial_collection_type"
                      value="private"
                      checked={formData.commercial_collection_type === 'private'}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Private Collection</span>
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500">Commercial waste is typically collected by private contractors.</p>
              </div>
            </div>
          </div>
        )}

        {/* Bin Storage Configuration - Only for Mixed-Use */}
        {isMixedUse && (
          <div className="border-t border-gray-200 pt-6 mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Bin Storage Configuration</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                How will residential and commercial bins be stored? <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                <label className="flex items-start p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="bin_storage_type"
                    value="shared"
                    checked={formData.bin_storage_type === 'shared'}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 mt-0.5"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900">Shared Bin Storage Area</span>
                    <p className="text-xs text-gray-500">Residential and commercial bins stored in the same area (bins will be clearly labelled)</p>
                  </div>
                </label>
                <label className="flex items-start p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="bin_storage_type"
                    value="separate"
                    checked={formData.bin_storage_type === 'separate'}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 mt-0.5"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900">Separate Bin Storage Areas</span>
                    <p className="text-xs text-gray-500">Dedicated storage areas for residential and commercial bins</p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Site Location Image (Top-View)
          </label>
          <div 
            className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
              isDragging 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-blue-400'
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div className="space-y-1 text-center">
              {imagePreview ? (
                <div>
                  <img src={imagePreview} alt="Site preview" className="mx-auto h-32 w-auto rounded" />
                  <button
                    type="button"
                    onClick={() => {
                      setSiteImage(null);
                      setImagePreview(null);
                    }}
                    className="mt-2 text-sm text-red-600 hover:text-red-500"
                  >
                    Remove image
                  </button>
                </div>
              ) : (
                <>
                  <svg
                    className={`mx-auto h-12 w-12 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`}
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="site-image-upload"
                      className="relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                    >
                      <span>Upload a file</span>
                      <input
                        id="site-image-upload"
                        name="site-image-upload"
                        type="file"
                        accept=".jpg,.jpeg,.tiff,.tif,.png"
                        onChange={handleImageChange}
                        className="sr-only"
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">JPEG, TIFF, or PNG up to 10MB</p>
                  {isDragging && (
                    <p className="text-sm text-blue-600 font-medium">Drop your image here</p>
                  )}
                </>
              )}
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Upload a top-view map showing the development and surrounding area (Figure 1: Site Location)
          </p>
        </div>

        <div className="flex justify-between pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : project ? 'Continue to Development Parameters' : 'Create Project & Continue'}
          </button>
        </div>
      </form>

      {/* NSW Enquiry Modal */}
      <NSWEnquiryModal 
        isOpen={showNSWModal} 
        onClose={() => setShowNSWModal(false)} 
      />
    </div>
  );
};

export default Step1ProjectDetails;
